import { useCallback, useEffect, useState } from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { rentalService } from "@/features/rentals/services/rental.service";
import { ClientCreateDialog } from "@/features/rentals/components/rental-create/ClientCreateDialog";
import { ContractCreateDialog } from "@/features/rentals/components/rental-create/ContractCreateDialog";
import { RentalCreateWizard } from "@/features/rentals/components/rental-create/wizard/RentalCreateWizard";
import {
  rentalCreateSchema,
  type RentalCreateFormSchema,
} from "@/features/rentals/components/rental-create/rental-create-schema";
import {
  buildCreatePayload,
  type NewClientData,
  type NewContractData,
  type RentalCreateFormState,
} from "@/features/rentals/utils/rental-create-payload";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { formatCPF, formatPhone } from "@/shared/lib/formatters";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { RentalType } from "@/shared/types/enums";

const DEFAULT_VALUES: RentalCreateFormSchema = {
  rentalType: RentalType.SUIT,
  sellerId: null,
  hasShoes: false,
  shoesPairQuantity: 0,
  totalValue: 0,
  payments: [],
  expectedOutAt: "",
  expectedInAt: "",
  description: "",
  emitInvoice: true,
  invoiceObservation: "",
  selectedClientId: null,
  newClient: null,
  selectedContractId: null,
  newContract: null,
  parentContractId: null,
  stockIds: [],
  gownItems: [],
  tagIds: [],
};

export function NovoAluguelPage() {
  const form = useForm<RentalCreateFormSchema>({
    resolver: zodResolver(rentalCreateSchema),
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  const rentalType = form.watch("rentalType");

  const [stockItems, setStockItems] = useState<StockAvailabilityResponseDTO[]>(
    [],
  );
  const [sellerLabel, setSellerLabel] = useState<string | null>(null);
  const [selectedClientLabel, setSelectedClientLabel] = useState<string | null>(
    null,
  );
  const [selectedContractLabel, setSelectedContractLabel] = useState<
    string | null
  >(null);
  const [parentContractLabel, setParentContractLabel] = useState<string | null>(
    null,
  );
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);

  const isDirty = form.formState.isDirty;

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
    [isDirty],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleTabChange = useCallback(
    (next: RentalType.SUIT | RentalType.GOWN) => {
      if (next === rentalType) return;
      form.setValue("rentalType", next, { shouldDirty: true });
      if (next === RentalType.GOWN) {
        form.setValue("stockIds", [], { shouldDirty: true });
        form.setValue("hasShoes", false, { shouldDirty: true });
        form.setValue("shoesPairQuantity", 0, { shouldDirty: true });
        setStockItems([]);
      } else {
        form.setValue("gownItems", [], { shouldDirty: true });
      }
    },
    [form, rentalType],
  );

  const handleSellerChange = useCallback(
    (sellerId: number | null, sellerName: string | null) => {
      form.setValue("sellerId", sellerId, { shouldDirty: true });
      setSellerLabel(sellerName);
    },
    [form],
  );

  const handleClientSelect = useCallback(
    (client: ClientResponseDTO) => {
      form.setValue("selectedClientId", client.id, { shouldDirty: true });
      form.setValue("newClient", null, { shouldDirty: true });
      setSelectedClientLabel(
        `${client.name} · ${formatCPF(client.document)} · ${formatPhone(client.phone)}`,
      );
    },
    [form],
  );

  const handleClientCreate = useCallback(
    (data: NewClientData) => {
      form.setValue("newClient", data, { shouldDirty: true });
      form.setValue("selectedClientId", null, { shouldDirty: true });
      setSelectedClientLabel(null);
      setClientDialogOpen(false);
    },
    [form],
  );

  const handleClientClear = useCallback(() => {
    form.setValue("selectedClientId", null, { shouldDirty: true });
    form.setValue("newClient", null, { shouldDirty: true });
    setSelectedClientLabel(null);
  }, [form]);

  const handleContractSelect = useCallback(
    (contract: ContractResponseDTO) => {
      form.setValue("selectedContractId", contract.id, { shouldDirty: true });
      form.setValue("newContract", null, { shouldDirty: true });
      setSelectedContractLabel(
        `Nº ${contract.contractNumber} · Cliente ${contract.clientName}`,
      );
    },
    [form],
  );

  const handleContractCreate = useCallback(
    (data: NewContractData) => {
      form.setValue("newContract", data, { shouldDirty: true });
      form.setValue("selectedContractId", null, { shouldDirty: true });
      setSelectedContractLabel(null);
      setContractDialogOpen(false);
    },
    [form],
  );

  const handleContractClear = useCallback(() => {
    form.setValue("selectedContractId", null, { shouldDirty: true });
    form.setValue("newContract", null, { shouldDirty: true });
    setSelectedContractLabel(null);
  }, [form]);

  const handleParentContractSelect = useCallback(
    (contract: ContractResponseDTO) => {
      form.setValue("parentContractId", contract.id, { shouldDirty: true });
      setParentContractLabel(
        `Nº ${contract.contractNumber} · Cliente ${contract.clientName}`,
      );
    },
    [form],
  );

  const handleParentContractClear = useCallback(() => {
    form.setValue("parentContractId", null, { shouldDirty: true });
    setParentContractLabel(null);
  }, [form]);

  const handleStockAdd = useCallback(
    (item: StockAvailabilityResponseDTO) => {
      const ids = form.getValues("stockIds");
      form.setValue("stockIds", [...ids, item.stockId], {
        shouldDirty: true,
      });
      setStockItems((prev) => [...prev, item]);
    },
    [form],
  );

  const handleStockRemove = useCallback(
    (stockId: number) => {
      const ids = form.getValues("stockIds");
      form.setValue(
        "stockIds",
        ids.filter((id) => id !== stockId),
        { shouldDirty: true },
      );
      setStockItems((prev) => prev.filter((item) => item.stockId !== stockId));
    },
    [form],
  );

  const resetAll = useCallback(() => {
    form.reset(DEFAULT_VALUES);
    setStockItems([]);
    setSellerLabel(null);
    setSelectedClientLabel(null);
    setSelectedContractLabel(null);
    setParentContractLabel(null);
  }, [form]);

  const handleSubmit = useCallback(async () => {
    const valid = await form.trigger();
    if (!valid) {
      toast.error("Verifique os campos obrigatórios");
      return;
    }
    const data = form.getValues();
    if (!data.sellerId) {
      toast.error("Selecione um vendedor");
      return;
    }
    const state: RentalCreateFormState = {
      rentalType: data.rentalType,
      sellerId: data.sellerId,
      hasShoes: data.hasShoes,
      shoesPairQuantity: data.shoesPairQuantity,
      totalValue: data.totalValue,
      payments: data.payments,
      expectedOutAt: data.expectedOutAt,
      expectedInAt: data.expectedInAt,
      description: data.description,
      emitInvoice: data.emitInvoice,
      invoiceObservation: data.invoiceObservation,
      selectedClientId: data.selectedClientId,
      newClient: data.newClient,
      selectedContractId: data.selectedContractId,
      newContract: data.newContract,
      parentContractId: data.parentContractId,
      stockIds: data.stockIds,
      gownItems: data.gownItems,
      tagIds: data.tagIds,
    };

    setSubmitting(true);
    try {
      await rentalService.create(buildCreatePayload(state));
      toast.success("Aluguel criado com sucesso");
      resetAll();
      setWizardKey((k) => k + 1);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, "Erro ao criar aluguel");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }, [form, resetAll]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Novo Aluguel</h1>
        <p className="text-muted-foreground">
          Preencha as etapas abaixo. As peças são opcionais — você pode salvar o
          aluguel incompleto e completá-lo depois.
        </p>
      </div>

      <RentalCreateWizard
        key={wizardKey}
        form={form}
        stockItems={stockItems}
        sellerLabel={sellerLabel}
        selectedClientLabel={selectedClientLabel}
        selectedContractLabel={selectedContractLabel}
        parentContractLabel={parentContractLabel}
        isSubmitting={submitting}
        onTypeChange={handleTabChange}
        onSellerChange={handleSellerChange}
        onClientSelect={handleClientSelect}
        onClientCreateOpen={() => setClientDialogOpen(true)}
        onClientClear={handleClientClear}
        onContractSelect={handleContractSelect}
        onContractCreateOpen={() => setContractDialogOpen(true)}
        onContractClear={handleContractClear}
        onParentContractSelect={handleParentContractSelect}
        onParentContractClear={handleParentContractClear}
        onStockAdd={handleStockAdd}
        onStockRemove={handleStockRemove}
        onSubmit={handleSubmit}
      />

      <ClientCreateDialog
        open={clientDialogOpen}
        defaultValues={form.getValues("newClient")}
        onClose={() => setClientDialogOpen(false)}
        onSubmit={handleClientCreate}
      />

      <ContractCreateDialog
        open={contractDialogOpen}
        defaultValues={form.getValues("newContract")}
        onClose={() => setContractDialogOpen(false)}
        onSubmit={handleContractCreate}
      />

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(value) => {
          if (!value && blocker.state === "blocked") blocker.reset();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sair sem salvar?</DialogTitle>
            <DialogDescription>
              Você tem dados preenchidos que serão perdidos se sair desta tela
              agora.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                blocker.state === "blocked" ? blocker.reset() : null
              }
            >
              Continuar editando
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() =>
                blocker.state === "blocked" ? blocker.proceed() : null
              }
            >
              Sair sem salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
